import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_bold_right, QIcon_close_default } from '@repo/icons/index'
import React, { useState } from 'react'
import useIsMobile from '../../modules/hooks/common/useIsMobile'

import { COLORS } from '../../modules/vars'

export const enum MESSAGE_CARD_SIZE {
  SM = 'sm',
  LG = 'lg',
}

export type MESSAGE_CARD_SIZE_KEY = MESSAGE_CARD_SIZE[keyof MESSAGE_CARD_SIZE]

interface MessageCardProps {
  bgColor?: string
  size?: MESSAGE_CARD_SIZE_KEY
  icon: React.ReactNode
  title: string
  description?: string
  zIndex?: number
  onClickClose: (e: React.MouseEvent<HTMLButtonElement>) => void
  buttonTitle?: string
  buttonColor?: string
  onClickButton?: (e) => void
  showMobileButton?: boolean
}

const MessageCard = ({
  bgColor = COLORS.PURPLE50,
  size = MESSAGE_CARD_SIZE.SM,
  icon,
  title,
  description = '',
  zIndex = 0,
  onClickClose,
  buttonTitle = '',
  buttonColor = '',
  onClickButton = () => {},
  showMobileButton = false,
}: MessageCardProps) => {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(true)
  if (!open) return null

  return (
    <>
      {size == MESSAGE_CARD_SIZE.LG ? (
        <div
          className="flex w-full justify-between flex-row items-center rounded-[8px] p-[16px]"
          style={{ backgroundColor: bgColor, zIndex: zIndex }}
        >
          <div className=" flex flex-row w-full items-center gap-[16px]">
            <div className="flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-full bg-white">
              {icon}
            </div>
            <div className="flex flex-col w-full gap-[4px]">
              <div className="Body1-16Bd truncate-2-lines text-left">{title}</div>
              {description.length > 1 && <div className="Body2-14Md text-left text-gray-700">{description}</div>}
            </div>
          </div>

          <div className="flex items-center flex-row">
            {buttonTitle && (
              <div className="ml-[32px] flex">
                <DSButton
                  type={BUTTON_TYPE.OUTLINE}
                  size={BUTTON_SIZE.XS}
                  customBorderColor={buttonColor}
                  onClick={(e) => onClickButton(e)}
                >
                  <DSButton.Label className="truncate" customTxtColor={buttonColor}>
                    {buttonTitle}
                  </DSButton.Label>
                </DSButton>
              </div>
            )}
            <button
              className="ml-[16px] flex cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()

                onClickClose(e)
                setOpen(false)
              }}
            >
              <QIcon_close_default width={20} height={20} color={COLORS.GRAY500} />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="flex min-h-fit w-full items-center justify-between flex-row rounded-[8px] p-[16px] gap-[16px] cursor-pointer"
          style={{ backgroundColor: bgColor, zIndex: zIndex }}
          onClick={(e) => onClickButton(e)}
        >
          <div className="flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-full bg-white">
            {icon}
          </div>
          <div className="flex flex-col w-full gap-4">
            <div className="flex flex-row h-full justify-between items-start">
              <div className="Body1-16Bd truncate-2-lines text-left">{title}</div>
              <button
                className="ml-[16px] flex cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()

                  onClickClose(e)
                  setOpen(false)
                }}
              >
                <QIcon_close_default width={20} height={20} color={COLORS.GRAY500} />
              </button>
            </div>
            {description.length > 1 && <div className="Body2-14Md text-left text-gray-700">{description}</div>}
            {buttonTitle && showMobileButton && (
              <div className="flex self-end flex-row gap-[2px] items-center">
                <div className="Body2-14Bd">{buttonTitle}</div>
                <QIcon_chevron_bold_right width={16} height={16} color={COLORS.GRAY500} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default MessageCard
